/**
 * Icon shim — the single place the app names an icon library (Solar Icons; PO ruling
 * 2026-09-23 replaced Lucide, PO ruling 2026-10-05 set the style mix below).
 *
 * RULE: filled by default; duotone only for wayfinding and illustration (nav, section strip,
 * empty/error states, dashboard tiles). Buttons, chevrons, close/menu, table actions, inline
 * status glyphs, the sidebar toggle and the firm-switcher chevron all stay filled.
 *
 *   Filled  = Solar "Bold"          → the named exports below. They keep the names the call
 *             sites already use (the old lucide names), so a screen only ever changed its
 *             import path; `size` / `aria-hidden="true"` / `className` pass straight through
 *             to the Solar component (an `<svg>` accepting every SVG prop).
 *   Duotone = Solar "Bold Duotone"  → the `Duotone` namespace: an ALLOWLIST keyed by the same
 *             names (plus five case-section-strip icons that have no filled twin). Add an entry
 *             only for a new nav / strip / state / tile use, never for chrome.
 *
 * Per-icon subpath imports (not the `@solar-icons/react/bold` barrel) so the dev server and
 * vitest never load the other ~2,700 modules. Every kebab name below is verified against
 * `node_modules/@solar-icons/react/dist/icons/{bold,bold-duotone}/*.mjs` (export names are
 * identical across Solar styles).
 *
 * Duotone second tone: `--argus-icon-secondary` (globals.css) feeds `--solar-secondary-color`
 * on `:root` (portal-safe) and via <SolarProvider> in main.tsx / design/main.tsx. The generated
 * bold-duotone markup paints its secondary layer with
 * `color: var(--solar-secondary-color, currentColor); opacity: var(--solar-secondary-opacity, 0.5)`
 * — the same mechanism line-duotone used — so the provider's `secondaryOpacity={1}` makes the
 * brand blue solid. In SEMANTIC colour contexts spread `DUOTONE_TINT` (below) instead.
 *
 * Credit: Solar Icons by 480 Design, CC BY 4.0 — see README.md "Credits".
 */
import type { Icon } from '@solar-icons/react/lib/types';
// Bold Duotone sources for the `Duotone` allowlist below (import aliases keep them apart
// from the filled export of the same glyph).
import { BillListIcon as BillListDuotone } from '@solar-icons/react/bold-duotone/bill-list';
import { Buildings2Icon as Buildings2Duotone } from '@solar-icons/react/bold-duotone/buildings-2';
import { DocumentIcon as DocumentDuotone } from '@solar-icons/react/bold-duotone/document';
import { DocumentTextIcon as DocumentTextDuotone } from '@solar-icons/react/bold-duotone/document-text';
import { LibraryIcon as LibraryDuotone } from '@solar-icons/react/bold-duotone/library';
import { MagnifierIcon as MagnifierDuotone } from '@solar-icons/react/bold-duotone/magnifier';
import { BookIcon as BookDuotone } from '@solar-icons/react/bold-duotone/book';
import { BuildingsIcon as BuildingsDuotone } from '@solar-icons/react/bold-duotone/buildings';
import { CaseRoundIcon as CaseRoundDuotone } from '@solar-icons/react/bold-duotone/case-round';
import { ChatSquareIcon as ChatSquareDuotone } from '@solar-icons/react/bold-duotone/chat-square';
import { CheckCircleIcon as CheckCircleDuotone } from '@solar-icons/react/bold-duotone/check-circle';
import { ClipboardTextIcon as ClipboardTextDuotone } from '@solar-icons/react/bold-duotone/clipboard-text';
import { DangerTriangleIcon as DangerTriangleDuotone } from '@solar-icons/react/bold-duotone/danger-triangle';
import { DocumentsIcon as DocumentsDuotone } from '@solar-icons/react/bold-duotone/documents';
import { FileSearchIcon as FileSearchDuotone } from '@solar-icons/react/bold-duotone/file-search';
import { FileWarningIcon as FileWarningDuotone } from '@solar-icons/react/bold-duotone/file-warning';
import { HistoryIcon as HistoryDuotone } from '@solar-icons/react/bold-duotone/history';
import { InboxIcon as InboxDuotone } from '@solar-icons/react/bold-duotone/inbox';
import { InfoCircleIcon as InfoCircleDuotone } from '@solar-icons/react/bold-duotone/info-circle';
import { MagnifierZoomInIcon as MagnifierZoomInDuotone } from '@solar-icons/react/bold-duotone/magnifier-zoom-in';
import { ShieldCheckIcon as ShieldCheckDuotone } from '@solar-icons/react/bold-duotone/shield-check';
import { UsersGroupRoundedIcon as UsersGroupRoundedDuotone } from '@solar-icons/react/bold-duotone/users-group-rounded';
import { UsersGroupTwoRoundedIcon as UsersGroupTwoRoundedDuotone } from '@solar-icons/react/bold-duotone/users-group-two-rounded';
import { WidgetIcon as WidgetDuotone } from '@solar-icons/react/bold-duotone/widget';

// Linear (outline) sources for the `Linear` allowlist below.
import { AltArrowLeftIcon as AltArrowLeftLinear } from '@solar-icons/react/linear/alt-arrow-left';
import { AltArrowRightIcon as AltArrowRightLinear } from '@solar-icons/react/linear/alt-arrow-right';
import { HamburgerMenuIcon as HamburgerMenuLinear } from '@solar-icons/react/linear/hamburger-menu';
import { SidebarCloseIcon as SidebarCloseLinear } from '@solar-icons/react/linear/sidebar-close';
import { SidebarOpenIcon as SidebarOpenLinear } from '@solar-icons/react/linear/sidebar-open';

export type IconComponent = Icon;

/* ── Filled (default) — Solar Bold ────────────────────────────────────────────────────── */
export { DangerCircleIcon as AlertCircle } from '@solar-icons/react/bold/danger-circle';
export { DangerTriangleIcon as AlertTriangle } from '@solar-icons/react/bold/danger-triangle';
export { BookIcon as BookOpen } from '@solar-icons/react/bold/book';
export { CaseRoundIcon as Briefcase } from '@solar-icons/react/bold/case-round';
export { BuildingsIcon as Building2 } from '@solar-icons/react/bold/buildings';
export { CalendarIcon as Calendar } from '@solar-icons/react/bold/calendar';
export { CameraIcon as Camera } from '@solar-icons/react/bold/camera';
export { CheckIcon as Check } from '@solar-icons/react/bold/check';
export { CheckCircleIcon as CheckCircle2 } from '@solar-icons/react/bold/check-circle';
export { AltArrowDownIcon as ChevronDown } from '@solar-icons/react/bold/alt-arrow-down';
export { AltArrowLeftIcon as ChevronLeft } from '@solar-icons/react/bold/alt-arrow-left';
export { AltArrowRightIcon as ChevronRight } from '@solar-icons/react/bold/alt-arrow-right';
export { AltArrowUpIcon as ChevronUp } from '@solar-icons/react/bold/alt-arrow-up';
export { SortVerticalIcon as ChevronsUpDown } from '@solar-icons/react/bold/sort-vertical';
export { ClipboardCheckIcon as ClipboardCheck } from '@solar-icons/react/bold/clipboard-check';
export { CopyIcon as Copy } from '@solar-icons/react/bold/copy';
export { DownloadMinimalisticIcon as Download } from '@solar-icons/react/bold/download-minimalistic';
export { EyeIcon as Eye } from '@solar-icons/react/bold/eye';
export { EyeClosedIcon as EyeOff } from '@solar-icons/react/bold/eye-closed';
export { FileSearchIcon as FileSearch } from '@solar-icons/react/bold/file-search';
export { DocumentTextIcon as FileText } from '@solar-icons/react/bold/document-text';
export { FileWarningIcon as FileWarning } from '@solar-icons/react/bold/file-warning';
export { GalleryIcon as Image } from '@solar-icons/react/bold/gallery';
export { InboxIcon as Inbox } from '@solar-icons/react/bold/inbox';
export { InfoCircleIcon as Info } from '@solar-icons/react/bold/info-circle';
export { Buildings2Icon as Landmark } from '@solar-icons/react/bold/buildings-2';
export { WidgetIcon as LayoutDashboard } from '@solar-icons/react/bold/widget';
export { LibraryIcon as Library } from '@solar-icons/react/bold/library';
/** Spinner: call sites apply the spin class themselves (e.g. `argus-btn__spinner`). */
export { RefreshIcon as Loader2 } from '@solar-icons/react/bold/refresh';
export { LockKeyholeMinimalisticIcon as Lock } from '@solar-icons/react/bold/lock-keyhole-minimalistic';
export { Login2Icon as LogIn } from '@solar-icons/react/bold/login-2';
export { Logout2Icon as LogOut } from '@solar-icons/react/bold/logout-2';
export { HamburgerMenuIcon as Menu } from '@solar-icons/react/bold/hamburger-menu';
export { ChatSquareIcon as MessageSquare } from '@solar-icons/react/bold/chat-square';
export { MoonIcon as Moon } from '@solar-icons/react/bold/moon';
export { MenuDotsIcon as MoreHorizontal } from '@solar-icons/react/bold/menu-dots';
export { SidebarCloseIcon as PanelLeftClose } from '@solar-icons/react/bold/sidebar-close';
export { SidebarOpenIcon as PanelLeftOpen } from '@solar-icons/react/bold/sidebar-open';
export { PenIcon as Pencil } from '@solar-icons/react/bold/pen';
export { AddIcon as Plus } from '@solar-icons/react/bold/add';
export { PrinterIcon as Printer } from '@solar-icons/react/bold/printer';
export { RefreshIcon as RefreshCw } from '@solar-icons/react/bold/refresh';
export { RestartIcon as RotateCcw } from '@solar-icons/react/bold/restart';
export { DisketteIcon as Save } from '@solar-icons/react/bold/diskette';
export { ScaleIcon as Scale } from '@solar-icons/react/bold/scale';
export { MagnifierZoomInIcon as ScanSearch } from '@solar-icons/react/bold/magnifier-zoom-in';
export { DocumentIcon as ScrollText } from '@solar-icons/react/bold/document';
export { MagnifierIcon as Search } from '@solar-icons/react/bold/magnifier';
export { ShareIcon as Share } from '@solar-icons/react/bold/share';
export { ShieldCheckIcon as ShieldCheck } from '@solar-icons/react/bold/shield-check';
export { ShieldCrossIcon as ShieldOff } from '@solar-icons/react/bold/shield-cross';
export { MagicWand3Icon as Sparkles } from '@solar-icons/react/bold/magic-wand-3';
export { SunIcon as Sun } from '@solar-icons/react/bold/sun';
export { TrashBinMinimalisticIcon as Trash2 } from '@solar-icons/react/bold/trash-bin-minimalistic';
export { LockKeyholeMinimalisticUnlockedIcon as Unlock } from '@solar-icons/react/bold/lock-keyhole-minimalistic-unlocked';
export { UploadIcon as Upload } from '@solar-icons/react/bold/upload';
export { CloudUploadIcon as UploadCloud } from '@solar-icons/react/bold/cloud-upload';
export { UserPlusIcon as UserPlus } from '@solar-icons/react/bold/user-plus';
export { UsersGroupRoundedIcon as Users } from '@solar-icons/react/bold/users-group-rounded';
export { WiFiOffIcon as WifiOff } from '@solar-icons/react/bold/wi-fi-off';
export { CloseIcon as X } from '@solar-icons/react/bold/close';

/* ── Duotone (allowlist) — Solar Bold Duotone ─────────────────────────────────────────── */
/**
 * The duotone allowlist. Keys mirror the filled export of the same glyph where one exists
 * (`Duotone.ShieldCheck` is the two-tone twin of `ShieldCheck`); the five section-strip icons
 * have no filled twin and take lucide-style names. Usage: `<Duotone.Inbox size={22} />`.
 */
export const Duotone = {
  // Sidebar navigation items (layouts/sidebar.tsx).
  LayoutDashboard: WidgetDuotone,
  Briefcase: CaseRoundDuotone,
  BookOpen: BookDuotone,
  Users: UsersGroupRoundedDuotone,
  FileSearch: FileSearchDuotone,
  Building2: BuildingsDuotone,
  ShieldCheck: ShieldCheckDuotone,
  // Case section strip (cases/case-nav.tsx): overview, statements, counterparties, reports, audit.
  ClipboardList: ClipboardTextDuotone,
  Receipt: BillListDuotone,
  UsersRound: UsersGroupTwoRoundedDuotone,
  Files: DocumentsDuotone,
  History: HistoryDuotone,
  // Empty / error / notice illustrations (ui/states.tsx).
  Inbox: InboxDuotone,
  AlertTriangle: DangerTriangleDuotone,
  Info: InfoCircleDuotone,
  CheckCircle2: CheckCircleDuotone,
  // Dashboard alert tiles (dashboard/dashboard-view.tsx) — plus AlertTriangle / ShieldCheck above.
  ScanSearch: MagnifierZoomInDuotone,
  FileWarning: FileWarningDuotone,
  MessageSquare: ChatSquareDuotone,
  // Empty-state illustrations on the documents, audit, reports, reference and team screens.
  FileText: DocumentTextDuotone,
  ScrollText: DocumentDuotone,
  Landmark: Buildings2Duotone,
  Library: LibraryDuotone,
  Search: MagnifierDuotone,
} as const satisfies Record<string, Icon>;

/**
 * Linear = Solar "Linear" (outline): an ALLOWLIST for the shell controls the PO wants unfilled
 * (2026-10-26): the sidebar collapse/expand toggle, the history arrows and the mobile menu.
 * Keyed by the same names as the filled exports. Everything else stays Bold.
 */
export const Linear = {
  ChevronLeft: AltArrowLeftLinear,
  ChevronRight: AltArrowRightLinear,
  Menu: HamburgerMenuLinear,
  PanelLeftClose: SidebarCloseLinear,
  PanelLeftOpen: SidebarOpenLinear,
} as const satisfies Record<string, Icon>;

/**
 * Second tone for a duotone icon sitting in a SEMANTIC colour (danger / warning / success /
 * info banners, the dashboard alert tiles): a 50% tint of the icon's own colour — Solar's native
 * bold-duotone look — instead of the brand light blue the provider supplies. In bold-duotone the
 * secondary layer is the glyph's BODY (the triangle, the circle), so a brand-blue body under a
 * red "!" would stop reading as danger. Spread it: `<Duotone.Info {...DUOTONE_TINT} />`.
 * Wayfinding and neutral illustrations (nav, strip, empty states) keep the brand second tone.
 */
export const DUOTONE_TINT = { secondaryColor: 'currentColor', secondaryOpacity: 0.5 } as const;
